/**
 * @fileoverview Context centralizado para manejo del storeId
 * Soluciona el problema de fragmentación del storeId a través de la aplicación
 * Implementa patrón Singleton + Context para garantizar consistencia
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import userDataService from '../services/userDataService';

// Context para el storeId
const StoreContext = createContext({
  storeId: null,
  isLoading: true,
  error: null,
  refreshStoreId: () => {},
  setStoreIdOverride: () => {}
});

/**
 * Provider centralizado para el storeId
 * Garantiza que toda la aplicación use el mismo storeId
 */
export const StoreProvider = ({ children }) => {
  const [storeId, setStoreId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para obtener y actualizar el storeId
  const refreshStoreId = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Obtener storeId desde userDataService
      const currentStoreId = userDataService.getStoreId();
      
      if (!currentStoreId) {
        // Intentar inicializar userData si no existe
        await userDataService.initializeUserData();
        const retryStoreId = userDataService.getStoreId();
        
        if (!retryStoreId) {
          throw new Error('No se pudo obtener storeId. Verifica la configuración de la tienda.');
        }
        
        setStoreId(retryStoreId);
      } else {
        setStoreId(currentStoreId);
      }
      
      console.log(`[StoreContext] StoreId actualizado: ${currentStoreId || 'null'}`);
      
    } catch (err) {
      console.error('[StoreContext] Error obteniendo storeId:', err);
      setError(err.message);
      setStoreId(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Override manual del storeId (para debugging)
  const setStoreIdOverride = useCallback((overrideId) => {
    if (overrideId) {
      localStorage.setItem('storeIdOverride', overrideId);
      console.warn(`[StoreContext] Override aplicado: ${overrideId}`);
    } else {
      localStorage.removeItem('storeIdOverride');
      console.log('[StoreContext] Override removido');
    }
    refreshStoreId();
  }, [refreshStoreId]);

  // Inicializar al montar el componente
  useEffect(() => {
    refreshStoreId();
  }, [refreshStoreId]);

  // Escuchar cambios en userData
  useEffect(() => {
    const handleUserDataChange = () => {
      console.log('[StoreContext] UserData cambió, actualizando storeId...');
      refreshStoreId();
    };

    // Suscribirse a cambios en userDataService si tiene eventos
    if (userDataService.on) {
      userDataService.on('userDataChanged', handleUserDataChange);
      return () => userDataService.off('userDataChanged', handleUserDataChange);
    }
  }, [refreshStoreId]);

  const contextValue = {
    storeId,
    isLoading,
    error,
    refreshStoreId,
    setStoreIdOverride
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

/**
 * Hook para usar el storeId en cualquier componente
 * @returns {Object} { storeId, isLoading, error, refreshStoreId, setStoreIdOverride }
 */
export const useStoreId = () => {
  const context = useContext(StoreContext);
  
  if (!context) {
    throw new Error('useStoreId debe usarse dentro de un StoreProvider');
  }
  
  return context;
};

/**
 * Hook simplificado que solo devuelve el storeId
 * Para casos donde solo necesitas el ID sin estado de carga
 * @returns {string|null} storeId
 */
export const useStoreIdValue = () => {
  const { storeId } = useStoreId();
  return storeId;
};

/**
 * HOC para inyectar storeId como prop
 * @param {React.Component} WrappedComponent 
 * @returns {React.Component}
 */
export const withStoreId = (WrappedComponent) => {
  return function StoreIdInjectedComponent(props) {
    const { storeId, isLoading, error } = useStoreId();
    
    return (
      <WrappedComponent 
        {...props} 
        storeId={storeId}
        storeIdLoading={isLoading}
        storeIdError={error}
      />
    );
  };
};

export default StoreContext;
